// ---------------------------------------------------------------------------
// Fonte única de verdade de "quem está logado" no app.
//
// O estado vem do próprio Supabase via onAuthStateChange (INITIAL_SESSION,
// SIGNED_IN, TOKEN_REFRESHED, USER_UPDATED, SIGNED_OUT) + uma única leitura
// inicial de getSession(). A pessoa vira `null` APENAS quando o Supabase emite
// SIGNED_OUT (saída de verdade) ou quando, ao abrir, não há sessão guardada.
//
// IMPORTANTE: falha de rede/token NUNCA define user = null. Nada de getUser()
// espalhado dando "deslogado" à toa quando o sinal está fraco no evento.
// ---------------------------------------------------------------------------
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { descartarSessaoAntiga, ehRefreshTokenInvalido } from '@/lib/sessao';

interface AuthContexto {
  user: User | null;
  session: Session | null;
  /** Já sabemos o estado (mesmo que ainda seja "sem sessão")? */
  pronto: boolean;
}

const Contexto = createContext<AuthContexto>({ user: null, session: null, pronto: false });

export function useAuth(): AuthContexto {
  return useContext(Contexto);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [pronto, setPronto] = useState(false);
  const prontoRef = useRef(false);

  useEffect(() => {
    let ativo = true;

    const marcarPronto = () => {
      if (prontoRef.current) return;
      prontoRef.current = true;
      if (ativo) setPronto(true);
    };

    // Leitura inicial (uma só). getSession lê o que está guardado; renova se preciso.
    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!ativo) return;
        setSession(data.session ?? null);
        setUser(data.session?.user ?? null);
        marcarPronto();
      })
      .catch((e) => {
        // Sessão guardada que já não existe no servidor: limpa e segue como
        // "sem sessão", sem erro na tela e sem mandar para o login.
        if (ehRefreshTokenInvalido(e)) void descartarSessaoAntiga();
        marcarPronto();
      });

    const { data: sub } = supabase.auth.onAuthStateChange((evento, s) => {
      if (!ativo) return;
      if (evento === 'SIGNED_OUT') {
        setUser(null);
        setSession(null);
        marcarPronto();
        return;
      }
      if (s) {
        // Mantém o MESMO objeto `user` quando é a mesma pessoa. O Supabase emite
        // um novo objeto a cada TOKEN_REFRESHED/SIGNED_IN (dispara quando a pessoa
        // volta para o app); sem isso, toda página com `user` nas dependências de
        // um efeito recarregava sozinha (flicker e perda de estado da tela).
        setUser((anterior) =>
          evento !== 'USER_UPDATED' &&
          anterior &&
          s.user &&
          anterior.id === s.user.id
            ? anterior
            : s.user,
        );
        setSession(s);
        marcarPronto();
        return;
      }
      // Sem sessão no evento: só zera quando é a primeira leitura (ninguém
      // guardado neste aparelho). TOKEN_REFRESHED/USER_UPDATED sem sessão são
      // ignorados — nunca deslogam por falha passageira.
      if (evento === 'INITIAL_SESSION') {
        setUser(null);
        setSession(null);
      }
      marcarPronto();
    });

    return () => {
      ativo = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const valor = useMemo<AuthContexto>(() => ({ user, session, pronto }), [user, session, pronto]);

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}